import { Autocomplete, Box, FormControl, FormHelperText, Grid, TextField, Typography } from '@mui/material';
import { debounce } from '@mui/material/utils';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import axios from 'axios';
import { useTranslation } from 'next-i18next';
import { SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { UseFormReturn, useController, useFormContext } from 'react-hook-form';
import { buildAddress, type Suggestion } from './AddressAutoComplete.helpers';
import { usePatientInformationStore } from '@src/store';
export default function AddressAutoComplete({
  name,
  form,
  smartyKey,
  setAddress
}: {
  name: string;
  form: UseFormReturn;
  smartyKey: string;
  setAddress: (address: string) => void;
}) {
  const {
    t
  } = useTranslation('index');
  const {
    address
  } = usePatientInformationStore(store => store);
  const [value, setValue] = useState<Suggestion | null>(null);
  const [inputValue, setInputValue] = useState(address || '');
  const [options, setOptions] = useState<readonly Suggestion[]>([]);
  const [selected, setSelected] = useState('');
  const [open, setOpen] = useState(false);
  const {
    control
  } = useFormContext();
  const {
    field,
    formState: {
      errors
    }
  } = useController({
    name,
    control
  });
  const error = errors[name];
  const errorMessage = error?.message ? t('required') : null;
  const fetchSuggestions = useCallback(debounce(async (input: string) => {
    if (!input.trim()) {
      setOptions([]);
      setOpen(false);
      return;
    }
    const maxResults = 5;
    try {
      const url = `https://us-autocomplete-pro.api.smarty.com/lookup?key=${smartyKey}&max_results=${maxResults}&search=${input}${selected ? '&selected=' + encodeURIComponent(selected) : ''}`;
      const response = await axios.get(url);
      if (response.status === 200 && response.data?.suggestions?.length > 0) {
        setOptions(response.data.suggestions);
        setOpen(true);
      } else {
        setOptions([]);
        setOpen(false);
      }
    } catch (e) {
      console.error({
        e
      });
      setOptions([]);
      setOpen(false);
    }
  }, 400), [smartyKey, selected]);
  useEffect(() => {
    if (value && value.entries > 1) {
      const displayValue = `${value.street_line}${value.entries === 1 ? ' ' + (value.secondary || '') : ''}`;
      fetchSuggestions(displayValue);
    }
  }, [fetchSuggestions, value]);
  return <FormControl error={!!error} fullWidth data-sentry-element="FormControl" data-sentry-component="AddressAutoComplete" data-sentry-source-file="AddressAutoComplete.tsx">
			<Autocomplete getOptionLabel={option => typeof option === 'string' ? option : `${option.street_line}${option.entries === 1 ? ' ' + option.secondary : ''}`} filterOptions={x => x} options={options} open={open} freeSolo autoComplete includeInputInList filterSelectedOptions value={value || field.value || ''} onClose={() => setOpen(false)} onChange={(_: SyntheticEvent<Element, Event>, newValue: Suggestion | null) => {
      setValue(newValue);
      if (newValue) {
        const displayValue = `${newValue.street_line}${newValue.entries === 1 ? ' ' + (newValue.secondary || '') : ''}`;
        setInputValue(displayValue);
        field.onChange(displayValue);
        setAddress(displayValue);
        if (newValue.entries > 1) {
          setSelected(buildAddress({
            ...newValue
          }, true));
        } else {
          form.setValue('zipCode', newValue.zipcode);
          form.clearErrors('zipCode');
          form.setValue('city', newValue.city);
          form.clearErrors('city');
          form.setValue('state', newValue.state);
          form.clearErrors('state');
          form.setValue('address', displayValue);
          form.clearErrors('address');
          setOpen(false);
        }
      } else {
        setInputValue('');
        field.onChange('');
        setAddress('');
      }
    }} onInputChange={(_, newInputValue, reason) => {
      if (reason === 'input') {
        setSelected('');
        setInputValue(newInputValue);
        field.onChange(newInputValue);
        setAddress(newInputValue);

        // Only fetch when user types
        fetchSuggestions(newInputValue);
      }
    }} inputValue={inputValue} renderInput={params => <TextField error={!!error} {...params} label={t('streetAddress')} required />} renderOption={(props, option) => {
      const address = buildAddress({
        ...option
      });
      const matches = match(address, inputValue);
      const parts = parse(address, matches);
      return <li {...props} key={address}>
							<Grid container alignItems="center">
								<Grid item sx={{
            width: 'calc(100% - 44px)',
            wordWrap: 'break-word'
          }}>
									{parts.map((part, index) => <Box key={index} component="span">
											<Typography variant={part.highlight ? 'bodyMediumBold' : 'bodyMediumBook'}>
												{part.text}
											</Typography>
										</Box>)}
								</Grid>
							</Grid>
						</li>;
    }} data-sentry-element="Autocomplete" data-sentry-source-file="AddressAutoComplete.tsx" />
			{errorMessage && <FormHelperText>{String(errorMessage)}</FormHelperText>}
		</FormControl>;
}